import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, inr, fdate } from "@/api";
import { Printer, Download, ArrowLeft } from "lucide-react";

const WORD_ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const WORD_TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const twoWords = (n) => n < 20 ? WORD_ONES[n] : (WORD_TENS[Math.floor(n / 10)] + (n % 10 ? " " + WORD_ONES[n % 10] : ""));
const numWords = (num) => {
  let n = Math.round(Number(num));
  if (n === 0) return "Zero";
  const out = [];
  [[10000000, "Crore"], [100000, "Lakh"], [1000, "Thousand"]].forEach(([div, name]) => {
    if (n >= div) { out.push(`${numWords(Math.floor(n / div))} ${name}`); n %= div; }
  });
  if (n >= 100) { out.push(`${WORD_ONES[Math.floor(n / 100)]} Hundred`); n %= 100; }
  if (n) out.push(twoWords(n));
  return out.join(" ");
};

export default function PaymentPrint() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/payments").then((r) => setP(r.data.find((x) => x.payment_id === id) || null)).catch(() => {});
    api.get("/settings").then((r) => setCompany(r.data)).catch(() => {});
  }, [id]);

  if (!p || !company) return <div className="min-h-screen flex items-center justify-center text-slate-600">Loading…</div>;
  const isReceipt = p.kind === "receipt";
  const accent = isReceipt ? "text-emerald-700" : "text-red-700";

  return (
    <div className="min-h-screen bg-slate-200 py-8 print:py-0 print:bg-white">
      <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between print:hidden px-4">
        <Link to="/app/payments" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-600" data-testid="payment-print-back">
          <ArrowLeft className="w-4 h-4" /> Back to Payments
        </Link>
        <div className="flex gap-2">
          <a href={`${API}/payments/${p.payment_id}/pdf`} download data-testid="payment-print-pdf"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-emerald-500/50 transition-colors duration-150">
            <Download className="w-4 h-4" /> PDF
          </a>
          <button onClick={() => window.print()} data-testid="payment-print-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 transition-colors duration-150">
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-xl overflow-hidden print:shadow-none print:rounded-none" data-testid="payment-print-view">
        <div className="flex justify-between items-start p-8 border-b-2 border-emerald-600">
          <div>
            <img src="/logo.png" alt="GREENRGY" className="h-12 mb-2 bg-white rounded px-1" />
            <h2 className="text-lg font-bold text-slate-900">{company.company_name}</h2>
            <p className="text-xs text-slate-600">{company.address}</p>
            <p className="text-xs text-slate-600">{company.phone} · {company.email}</p>
            <p className="text-xs font-semibold text-slate-800">GSTIN: {company.gstin}</p>
          </div>
          <div className="text-right">
            <h1 className={`text-2xl font-extrabold tracking-wide ${accent}`} data-testid="payment-print-title">
              {isReceipt ? "PAYMENT RECEIPT" : "PAYMENT VOUCHER"}
            </h1>
            <p className="text-xs text-slate-500 italic">{isReceipt ? "Money received — acknowledgement" : "Money paid — outward payment"}</p>
            <p className="font-mono font-bold text-slate-900 mt-2" data-testid="payment-print-number">{p.number}</p>
            <p className="text-sm text-slate-600">Date: {fdate(p.date)}</p>
          </div>
        </div>

        <div className="p-8 space-y-6">
          <div className="flex justify-between items-start border-b border-slate-200 pb-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{isReceipt ? "Received From" : "Paid To"}</p>
              <p className="font-semibold text-slate-900 mt-1" data-testid="payment-print-party">{p.party_name}</p>
              <p className="text-xs text-slate-500 capitalize">{p.party_type}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Amount</p>
              <p className={`font-mono text-2xl font-extrabold ${accent}`} data-testid="payment-print-amount">{inr(p.amount)}</p>
            </div>
          </div>

          <table className="w-full text-sm">
            <tbody>
              {[
                ["Amount in words", `Rupees ${numWords(p.amount)} Only`],
                ["Payment mode", p.payment_mode || "—"],
                ["Account", p.account_name],
                ...(p.allocations?.length ? [["Adjusted against", p.allocations.map((a) => `${a.number} (${inr(a.amount)})`).join(", ")]] : []),
                ...(p.on_account > 0 ? [["Advance / On-account", inr(p.on_account)]] : []),
                ...(p.note ? [["Note", p.note]] : []),
              ].map(([k, v], i) => (
                <tr key={k} className={i % 2 ? "bg-slate-50" : ""}>
                  <td className="py-2 px-3 text-slate-500 font-semibold w-44">{k}</td>
                  <td className="py-2 px-3 text-slate-900">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-between items-end pt-10 border-t border-slate-200">
            <p className="text-xs text-slate-500">Received by: {p.created_by}</p>
            <div className="text-right">
              <p className="text-xs text-slate-600 mb-8">For {company.company_name}</p>
              <p className="text-xs font-bold text-slate-800 border-t border-slate-300 pt-1 px-6">Authorised Signatory</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
